<template>
  <article :id="`${featureName}-feature`" :class="['feature-card', computedClass]">
    <section class="header">
      <FeatureControlSection
        :title="title ?? ''"
        :fullscreen="sharedData.fullscreen"
        :show-back-button="showBackButton"
        :show-resize-button="showResizeButton && enableFullscreen"
        :info="info"
        @toggle-fullscreen="toggleFullscreen"
        @exit="exit"
        @go-back="() => setFeature(`home/${featureName}`)"
      />
    </section>
    <section class="content">
      <section v-if="route.path === `/home/${featureName}`" class="sub-features">
        <section v-for="feature in children" :key="feature.name" class="sub-section">
          <h1>{{ feature.title[locale] }}</h1>
          <div class="section-content">
            <template v-for="feat in feature.children" :key="feat.name">
              <DashboardFeatureIcon
                :id="`${feat.name}-sub-feature`"
                :caption="feat.titleShort?.[locale] ?? feat.title[locale]"
                :features="[feature.name, feat.name]"
              >
                <component :is="feat.props.icon" v-if="feat.props" />
              </DashboardFeatureIcon>
            </template>
          </div>
        </section>
      </section>
      <FeatureComponent :feature="`home/${featureName}/*/*`" />
    </section>
  </article>
</template>
<script lang="ts" setup>
import { computed, watch, provide, reactive, ref, onMounted, onUnmounted, toRefs } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FeatureControlSection from '@/design-system/molecules/FeatureControlSection.vue'
import { SharedData, sharedDataKey } from './types'
import useFeatures, { useFeature } from '@/pinia/features'
import { useI18n } from 'vue-i18n'
import FeatureComponent from './FeatureComponent.vue'
import DashboardFeatureIcon from './AnalyticsDashboard/components/DashboardFeatureIcon.vue'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { getRealmId } from '@/utils/realmId'
import { storeToRefs } from 'pinia'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selection'
import { PoiProperties } from '@/interfaces/poi.interface'
import { PoiInterface as navVisPoi } from '@navvis/ivion'
import { Feature } from '@/pinia/feature'

type Props = {
  title: string
  subtitle: LocaleString
  featureName: string
  enableFullscreen?: boolean
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  enableFullscreen: true,
  size: 'medium'
})

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const { feature: overviewFeature } = useFeature(`home/${props.featureName}`)
const { feature: subFeature } = useFeature(`home/${props.featureName}/*/*`)
const features = useFeatures()
const { resetMainFeature, pushFeatures, setFeature } = features
const { activeFeature } = toRefs(features)
const piniaStore = useGeneralStore()
const store = useStore()
const selectionStore = useSelectionStore()
const children = computed(() => overviewFeature.value?.children?.filter((c) => c.enabled) || [])
const { clickedEntityType, currentEntity } = storeToRefs(selectionStore)
const realmId = getRealmId()

const isPoiFromBackend =
  !(store.state.selectedPoi as unknown as navVisPoi)?.title &&
  !(store.state.selectedPoi as unknown as navVisPoi)?.pov

const width = computed(() => {
  switch (props.size) {
    case 'small':
      return '341px'
    case 'medium':
      return '539px'
    case 'large':
      return '539px'
  }
  return '539px'
})

const title = computed(() => {
  if (props.featureName === 'info') {
    if (clickedEntityType.value === 'REGION') {
      return realmId
    }
    if (clickedEntityType.value === 'POI') {
      return store.state.selectedPoi ? poiTitle(isPoiFromBackend, store.state.selectedPoi) : ''
    }
    if (clickedEntityType.value === 'LINE') {
      return piniaStore.selectedLine?.name ?? ''
    }
    if (clickedEntityType.value === 'ZONE') {
      return piniaStore.selectedZone?.name ?? ''
    }
    return currentEntity.value?.displayName ?? ''
  }
  if (router.currentRoute.value.path === `/home/${props.featureName}`) {
    return overviewFeature.value?.title?.[locale.value] ?? ''
  }
  return subFeature.value?.title[locale.value] ?? ''
})

const info = computed(() => {
  return subFeature.value?.info?.[locale.value] ?? ''
})

const sharedData = reactive<SharedData>({
  fullscreen: false,
  sectionIndex: 0,
  updateSectionIndex
})
provide(sharedDataKey, sharedData)

const showResizeButton = ref(false)
const showBackButton = ref(false)
const abortController = new AbortController()

function poiTitle(isPoiFromBackend: boolean, selectedPoi: PoiProperties): string {
  if (!isPoiFromBackend) return selectedPoi.title ?? ''
  return selectedPoi.name ? selectedPoi.name : selectedPoi.title ?? ''
}

const allChildren = computed<Feature[]>(() => {
  return children.value.flatMap((c) => c.children).filter((c) => c !== undefined) as Feature[]
})

watch(
  router.currentRoute,
  (value) => {
    if (value.path === `/home/${props.featureName}`) {
      showBackButton.value = false
      return
    }
    const children = allChildren.value?.filter((c) => c.enabled)
    if (children?.length === 1) {
      showBackButton.value = false
      return
    }
    showBackButton.value = true
  },
  { immediate: true }
)

function toggleFullscreen() {
  sharedData.fullscreen = !sharedData.fullscreen
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
}

function exit() {
  resetMainFeature()
}

onMounted(() => {
  sharedData.fullscreen = window.innerWidth <= 1024
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
  showResizeButton.value = window.innerWidth > 1024
  window.addEventListener('resize', () => resizeEventHandler(), { signal: abortController.signal })
  if (activeFeature.value?.name === props.featureName) {
    if (allChildren.value.length === 1 && allChildren.value[0].name) {
      pushFeatures([children.value[0].name, allChildren.value[0].name])
    }
  }
})

onUnmounted(() => {
  abortController.abort()
})

const mobileScreen = computed(() => {
  return store.state.mobileScreen
})

const computedClass = computed(() => {
  return {
    fullscreen: sharedData.fullscreen,
    mobile: mobileScreen.value === true
  }
})

function resizeEventHandler() {
  showResizeButton.value = window.innerWidth > 1024
  if (!sharedData.fullscreen) sharedData.fullscreen = window.innerWidth <= 1024
  piniaStore.setIsFeatureFullscreen(sharedData.fullscreen)
}

function updateSectionIndex(index: number) {
  sharedData.sectionIndex = index
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.test {
  padding: 20px;
}

section.content {
  z-index: 1000;
  display: flex;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  flex-direction: row;
}
section.header {
  padding: 24px 24px 0 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  h4 {
    all: unset;
    @include font-mixins.desktop-bold-h4;
    display: flex;
    flex-grow: 1;
    color: #1a1a1a;
  }
}
.top-container {
  flex-shrink: 0;
  padding: variables.$padding-main-body;
  padding-bottom: 0;
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
      all: unset;
      @include font-mixins.desktop-bold-h4;
    }
    button {
      border: none;
      border-radius: variables.$border-radius-small;
      display: flex;
      justify-content: center;
      align-items: center;
      background: none;
      padding: variables.$padding-medium;
      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.rhs-controls,
.lhs-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
}

section.sub-features {
  margin-top: 16px;
  padding: 0 24px 24px 24px;
  width: 100%;
  h1 {
    margin-top: 0;
    @include font-mixins.desktop-bold-label;
  }
  section .section-content {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
  }
}
.sub-section:not(:first-child) {
  margin-top: 16px;
}
.feature-card {
  background-color: white;
  position: fixed;
  display: flex;
  height: fit-content;
  flex-direction: column;
  left: variables.$feature-position;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  top: calc(#{variables.$layout-top-bar-height} + #{variables.$padding-xlarge});
  width: v-bind(width);
  max-height: calc(
    100vh - #{variables.$layout-top-bar-height} - #{variables.$padding-xlarge} - #{variables.$padding-xlarge} *
      2
  );
  z-index: 119;
  border-radius: 8px;
  &.fullscreen {
    border-radius: 0;
    left: 0;
    top: calc(#{variables.$layout-top-bar-height});
    width: 100vw;
    max-height: unset;
    height: calc(100vh - #{variables.$layout-top-bar-height});
  }

  &.mobile {
    border-radius: 0;
    left: 0;
    top: calc(#{variables.$layout-top-bar-height});
    width: 100vw;
    max-height: unset;
    height: calc(100vh - #{variables.$layout-top-bar-height});
  }
}
button.ghost {
  height: 32px;
  width: 32px;
  border: none;
  border-radius: variables.$border-radius-small;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}
#fullscreen-icon,
#dismiss-fullscreen-icon {
  width: 16px;
  height: 16px;
}
#exit-icon {
  width: 20px;
  height: 20px;
}
#info-feature {
  max-height: calc(
    100vh - #{variables.$layout-top-bar-height} - #{variables.$padding-xlarge} - 98px
  );
  @media screen and (max-width: 1024px) {
    max-height: unset;
  }
  .content {
    overflow: hidden;
  }
}
</style>
